import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ComparisonFlag, HaynesProCandidate } from '@/hooks/useVehicleTechnicalData';
import type { KnownTechnicalSnapshot } from './knownTechnicalCandidates';

const FLAG_COLOR: Record<ComparisonFlag, 'success' | 'error' | 'warning' | 'default'> = {
  matched: 'success',
  mismatch: 'error',
  missing: 'warning',
  unknown: 'default',
};

const FLAG_FIELDS: Array<{ key: keyof HaynesProCandidate['comparison']; labelKey: string }> = [
  { key: 'make', labelKey: 'make' },
  { key: 'model', labelKey: 'model' },
  { key: 'engineCode', labelKey: 'technicalData.engineCode' },
  { key: 'year', labelKey: 'technicalData.manufacturingYear' },
  { key: 'capacity', labelKey: 'technicalData.engineCapacity' },
  { key: 'output', labelKey: 'technicalData.engineOutput' },
];

export interface HaynesProCandidateListProps {
  candidates: readonly HaynesProCandidate[];
  preferredIds: ReadonlySet<string>;
  preferredCount: number;
  totalCount: number;
  otherCount: number;
  knownTechnical: KnownTechnicalSnapshot;
  filterQuery: string;
  onFilterQueryChange: (query: string) => void;
  showFilter: boolean;
  showOthers: boolean;
  onShowOthersChange: (show: boolean) => void;
  showOthersToggle: boolean;
  selectedCandidateId: string | null;
  onSelect: (candidateId: string) => void;
  disabled?: boolean;
}

function knownComparisonParts(
  known: KnownTechnicalSnapshot,
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const parts: string[] = [];
  if (known.output !== null) {
    parts.push(t('technicalData.knownComparisonOutput', { value: known.output }));
  }
  if (known.capacity !== null) {
    parts.push(t('technicalData.knownComparisonCapacity', { value: known.capacity }));
  }
  if (known.engineCode !== null) {
    parts.push(t('technicalData.knownComparisonEngine', { value: known.engineCode }));
  }
  return parts;
}

export function HaynesProCandidateList({
  candidates,
  preferredIds,
  preferredCount,
  totalCount,
  otherCount,
  knownTechnical,
  filterQuery,
  onFilterQueryChange,
  showFilter,
  showOthers,
  onShowOthersChange,
  showOthersToggle,
  selectedCandidateId,
  onSelect,
  disabled = false,
}: HaynesProCandidateListProps) {
  const { t } = useTranslation('vehicles');
  const knownParts = knownComparisonParts(knownTechnical, t);
  const hasPreferred = preferredCount > 0;

  return (
    <Box>
      {knownParts.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} role="status">
          {t('technicalData.knownComparisonSummary', { values: knownParts.join(' · ') })}
        </Typography>
      )}
      {hasPreferred && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} component="p">
            {t('technicalData.knownEngineMatchLabel')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('technicalData.preferredMatchCount', { matched: preferredCount, total: totalCount })}
          </Typography>
        </Stack>
      )}
      {showFilter && (
        <TextField
          value={filterQuery}
          onChange={(event) => onFilterQueryChange(event.target.value)}
          label={t('technicalData.filterCandidates')}
          disabled={disabled}
          fullWidth
          size="small"
          sx={{ mb: 1.5 }}
          slotProps={{ htmlInput: { 'aria-label': t('technicalData.filterCandidatesAria') } }}
        />
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('technicalData.candidateCount', { count: candidates.length })}
      </Typography>
      <Box
        role="region"
        aria-label={t('technicalData.candidateList')}
        sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}
      >
        {candidates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('technicalData.noCandidatesMatchFilter')}
          </Typography>
        ) : (
          <RadioGroup
            aria-label={t('technicalData.selectCandidate')}
            value={selectedCandidateId ?? 'none'}
            onChange={(event) => onSelect(event.target.value)}
          >
            <Stack spacing={1}>
              {candidates.map((item) => {
                const title = item.fullName ?? item.name ?? String(item.carTypeId);
                const optionLabel = t('technicalData.candidateOption', {
                  name: title,
                  engine: item.engineCode ?? t('technicalData.engineUnknown'),
                });
                const isPreferred = preferredIds.has(item.id);
                return (
                  <Box
                    key={item.id}
                    sx={{
                      border: 1,
                      borderColor: selectedCandidateId === item.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <FormControlLabel
                      value={item.id}
                      control={
                        <Radio
                          size="small"
                          disabled={disabled}
                          inputProps={{ 'aria-label': optionLabel }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[
                              item.engineCode,
                              item.capacity != null ? `${String(item.capacity)} cm³` : null,
                              item.output != null ? `${String(item.output)} kW` : null,
                              item.madeFrom || item.madeUntil
                                ? `${item.madeFrom ?? '…'}–${item.madeUntil ?? '…'}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                          {isPreferred && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('technicalData.knownEngineMatchLabel')}
                            </Typography>
                          )}
                        </Box>
                      }
                      slotProps={{ typography: { component: 'div' } }}
                      sx={{ alignItems: 'flex-start', mr: 0, width: '100%' }}
                    />
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5, ml: 4 }}>
                      {FLAG_FIELDS.map((field) => {
                        const flag = item.comparison[field.key];
                        return (
                          <Chip
                            key={field.key}
                            size="small"
                            color={FLAG_COLOR[flag]}
                            variant={flag === 'unknown' ? 'outlined' : 'filled'}
                            label={`${t(field.labelKey)}: ${t(`technicalData.flags.${flag}`)}`}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </RadioGroup>
        )}
      </Box>
      {showOthersToggle && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            type="button"
            variant="text"
            size="small"
            disabled={disabled}
            aria-expanded={showOthers}
            aria-label={
              showOthers
                ? t('technicalData.hideOtherCandidatesAria')
                : t('technicalData.showOtherCandidatesAria', { count: otherCount })
            }
            onClick={() => onShowOthersChange(!showOthers)}
          >
            {showOthers
              ? t('technicalData.hideOtherCandidates')
              : t('technicalData.showOtherCandidates', { count: otherCount })}
          </Button>
        </Box>
      )}
    </Box>
  );
}
